import { Routes } from '@angular/router';
import { B2bDeviceUtilService } from '@app/services/util-services/root-services/b2b-device-util.service';
import { IsAnonymousGuard } from '@app/services/guards/isAnonymous.guard';
import {
  DESKTOP_USER_DASHBOARD_ROUTES,
  MOBILE_USER_DASHBOARD_ROUTES,
} from '@b2b/user-dashboard-lib';
import { PAGE_TYPES } from '@app/constants/app-settings';

const promos = [
  'promos',
  'hotel-booking-offers',
  'resort-booking-offers',
  'bag2bag-hotel-discount-codes',
  'bag2bag-resort-discount-codes',
  'festival-offers',
  'diwali-hotel-offers',
  'diwali-resort-offers',
  'christmas-hotel-deals',
  'christmas-resort-deals',
  'new-year-hotel-promo-code',
  'new-year-resort-promo-code',
  'summer-hotel-offers',
  'summer-resort-offers',
  'winter-hotel-discounts',
  'winter-resort-discounts',
  'monsoon-hotel-offers',
  'monsoon-resort-offers',
  'valentines-day-hotel-promo-codes',
  'valentines-day-resort-promo-codes',
  'mothers-day-hotel-offers',
  'mothers-day-resort-offers',
  'independence-day-hotel-deals',
  'independence-day-resort-deals',
  'republic-day-hotel-offers',
  'republic-day-resort-offers',
  'black-friday-hotel-deals',
  'black-friday-resort-deals',
  'cyber-monday-hotel-discount-codes',
  'cyber-monday-resort-discount-codes',
  'hotels-in-city-with-promo-codes',
  'resorts-in-city-with-promo-codes',
  'book-hotel-with-promo-code',
  'certified',
];

export const getWebRoutes = (
  b2bDeviceUtilService: B2bDeviceUtilService,
): Routes => {
  return [
    ...WEB_ROUTES,
    ...getPromos(),
    ...createRouteMapping(b2bDeviceUtilService?.isDesktop()),
    ...setListPageRoutes(['**'], '', b2bDeviceUtilService?.isDesktop()),
  ];
};

const getPromos = () => {
  return promos?.map((item) => {
    return {
      path: item,
      loadChildren: () =>
        import('@b2b/promos-lib').then((m) => m.PROMOS_ROUTES),
    };
  });
};

export const WEB_ROUTES: Routes = [
  {
    path: 'auth',
    loadChildren: () => import('@b2b/auth-lib').then((m) => m.AuthModule),
    canActivate: [IsAnonymousGuard],
  },
  {
    path: 'careers',
    loadChildren: () => import('@b2b/career-lib').then((m) => m.CAREER_ROUTES),
  },
  {
    path: 'refer-and-earn',
    loadChildren: () =>
      import('@b2b/referral-code-lib').then((m) => m.REFERRAL_ROUTES),
  },
  {
    path: 'coupons',
    loadChildren: () =>
      import('@b2b/coupon-page-lib').then((m) => m.COUPON_ROUTES),
  },
  {
    path: 'feature/checkout',
    loadChildren: () =>
      import('@b2b/feature-checkout-lib').then(
        (m) => m.FEATURE_CHECKOUT_ROUTES,
      ),
    data: {
      loadOnClient: true,
    },
  },
  {
    path: 'feature/bookings',
    loadChildren: () =>
      import('@b2b/feature-booking-lib').then((m) => m.FEATURE_BOOKING_ROUTES),
    data: {
      loadOnClient: true,
    },
  },
  {
    path: 'page-not-found',
    loadChildren: () =>
      import('@b2b/page-not-found-lib').then((m) => m.PAGES_NOT_FOUND_ROUTES),
  },
  {
    path: 'contest',
    loadChildren: () => import('@b2b/offers').then((m) => m.OFFER_ROUTES),
  },
  {
    path: 'faqs',
    loadChildren: () => import('@b2b/faq-lib').then((m) => m.FAQS_ROUTES),
  },
  {
    path: 'company',
    loadChildren: () =>
      import('@b2b/company-lib').then((m) => m.COMPANY_PAGE_ROUTES),
  },
  {
    path: 'stories',
    loadChildren: () =>
      import('@b2b/character-lib').then((m) => m.CHARACTER_PAGE_ROUTES),
  },
  {
    path: 'social-impact',
    loadChildren: () =>
      import('@b2b/donation-lib').then((m) => m.DONATION_ROUTES),
  },
  {
    path: 'blogs',
    loadChildren: () => import('@b2b/blog-lib').then((m) => m.BLOG_ROUTES),
  },
  {
    path: 'membership',
    loadChildren: () =>
      import('@b2b/membership-lib').then((m) => m.MEMBERSHIP_ROUTES),
  },
  {
    path: 'rewards/free-stays',
    loadChildren: () =>
      import('@b2b/membership-lib').then((m) => m.MEMBERSHIP_ROUTES),
  },
  {
    path: 'partnership',
    loadChildren: () =>
      import('@b2b/partnership-lib').then((m) => m.PARTNERSHIP_ROUTES),
  },
  {
    path: 'world-cup',
    loadChildren: () => import('@b2b/venue-lib').then((m) => m.VENUE_ROUTES),
  },

  {
    path: 'brand-partnership',
    loadChildren: () =>
      import('@b2b/partner-ads-lib').then((m) => m.PARTNER_ROUTES),
  },
  {
    path: 'bag2bag-for-business',
    loadChildren: () =>
      import('@b2b/business-enquiry-lib').then(
        (m) => m.BUSINESS_ENQUIRY_ROUTES,
      ),
  },
  {
    path: 'hotel-booking-app-android',
    loadChildren: () =>
      import('@b2b/app-promotion-lib').then((m) => m.APP_PROMOTION_ROUTES),
    data: {
      pageType: 'ANDROID',
    },
  },
  {
    path: 'hotel-booking-app-ios',
    loadChildren: () =>
      import('@b2b/app-promotion-lib').then((m) => m.APP_PROMOTION_ROUTES),
    data: {
      pageType: 'IOS',
    },
  },
  {
    path: 'hourly-hotels-all-cities',
    loadChildren: () =>
      import('@b2b/sitemap-lib').then((m) => m.SITEMAP_ROUTES),
  },
  {
    path: 'hotel-stays-all-cities',
    loadChildren: () =>
      import('@b2b/sitemap-lib').then((m) => m.SITEMAP_ROUTES),
  },
  {
    path: 'homestays-all-cities',
    loadChildren: () =>
      import('@b2b/sitemap-lib').then((m) => m.SITEMAP_ROUTES),
  },
  {
    path: 'service-apartments-all-cities',
    loadChildren: () =>
      import('@b2b/sitemap-lib').then((m) => m.SITEMAP_ROUTES),
  },
  {
    path: 'resorts-all-cities',
    loadChildren: () =>
      import('@b2b/sitemap-lib').then((m) => m.SITEMAP_ROUTES),
  },
  {
    path: 'talk-to-ai',
    loadChildren: () => import('@b2b/ai-chat').then((m) => m.AI_CHAT_ROUTES),
  },
  {
    path: 'buy-gift-cards-online',
    loadChildren: () =>
      import('@b2b/desktop-web-gift-card-lib').then(
        (m) => m.DESKTOP_WEB_GIFT_CARD_ROUTES,
      ),
  },
];

const createRouteMapping = (isDesktop) => {
  let routeMappings: Routes = [];
  if (isDesktop) {
    routeMappings = [
      {
        path: 'feature/list',
        loadChildren: () =>
          import('@b2b/desktop-web-property-list-lib').then(
            (m) => m.DESKTOP_WEB_PROPERTY_LIST_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.LIST_PAGE,
          loadOnClient: true,
        },
      },
      {
        path: 'feature/detail',
        loadChildren: () =>
          import('@b2b/desktop-web-property-detail-lib').then(
            (m) => m.DESKTOP_DETAIL_PAGE_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.DETAIL_PAGE,
          loadOnClient: true,
        },
      },
      {
        path: 'feature/activity-detail',
        loadChildren: () =>
          import('@b2b/desktop-web-activity-detail-lib').then(
            (m) => m.DESKTOP_ACTIVITY_DETAIL_PAGE_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.ACTIVITY_DETAIL_PAGE,
          loadOnClient: true,
        },
      },
      ...DESKTOP_USER_DASHBOARD_ROUTES,
      ...setHomePageRoutes(
        [
          '',
          'hotels',
          'hotels-stay',
          'service-apartments',
          'homestays',
          'resorts',
          'experiences',
          'experiences/venues',
          'experiences/packages',
          'experiences/tour-travel-packages',
        ]?.reverse(),
        isDesktop,
      ),
    ];
  } else {
    routeMappings = [
      {
        path: 'feature/list',
        loadChildren: () =>
          import('@b2b/mob-web-property-list-lib').then(
            (m) => m.MOBILE_WEB_PROPERTY_LIST_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.LIST_PAGE,
          loadOnClient: true,
        },
      },
      {
        path: 'feature/detail',
        loadChildren: () =>
          import('@b2b/mobile-web-property-detail-lib').then(
            (m) => m.MOB_WEB_DETAIL_PAGE_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.DETAIL_PAGE,
          loadOnClient: true,
        },
      },
      {
        path: 'feature/activity-detail',
        loadChildren: () =>
          import('@b2b/mobile-web-activity-detail-lib').then(
            (m) => m.MOB_WEB_ACTIVITY_PAGE_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.ACTIVITY_DETAIL_PAGE,
          loadOnClient: true,
        },
      },
      ...MOBILE_USER_DASHBOARD_ROUTES,
      ...setFeatureHomePageRoutes(
        [
          'hotels',
          'hotels-stay',
          'service-apartments',
          'homestays',
          'resorts',
          'experiences',
          'experiences/venues',
          'experiences/packages',
          'experiences/tour-travel-packages',
        ]?.reverse(),
      ),
      ...setHomePageRoutes([''], isDesktop),
    ];
  }

  routeMappings = [
    ...setDetailPageRoutes(
      ['hotels', 'hotels-stay', 'service-apartments', 'homestays', 'resorts'],
      '/:cityName/:localityName/:propertyName',
      isDesktop,
    ),
    ...setActivityDetailPageRoutes(
      [
        'experiences/packages',
        'experiences/tour-travel-packages',
        'experiences/venues',
      ],
      '/:cityName/:dyParam/:propertyName',
      isDesktop,
    ),
    ...setListPageRoutes(
      [
        'hotels',
        'hotels-stay',
        'service-apartments',
        'homestays',
        'resorts',
        'experiences/packages',
        'experiences/tour-travel-packages',
        'experiences/venues',
      ],
      '/:cityName/:localityName',
      isDesktop,
    ),
    ...setListPageRoutes(
      [
        'hotels',
        'hotels-stay',
        'service-apartments',
        'homestays',
        'resorts',
        'experiences/packages',
        'experiences/tour-travel-packages',
        'experiences/venues',
      ],
      '/:cityName',
      isDesktop,
    ),
    ...routeMappings,
  ];

  return routeMappings;
};

const setHomePageRoutes = (paths: string[], isDesktop: boolean): Routes => {
  if (isDesktop) {
    const finalPaths: Routes = [];
    for (const path of paths) {
      finalPaths.push({
        path,
        loadChildren: () =>
          import('@b2b/desktop-web-home-page-lib').then(
            (m) => m.DESKTOP_WEB_HOME_PAGE_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.HOME_PAGE,
        },
      });
    }

    return finalPaths;
  } else {
    return paths.map((path) => {
      return {
        path,
        loadChildren: () =>
          import('@b2b/mobile-web-home-page-lib').then(
            (m) => m.MOBILE_WEB_HOME_PAGE_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.HOME_PAGE,
        },
      };
    });
  }
};

const setFeatureHomePageRoutes = (paths: string[]): Routes => {
  const finalPaths: Routes = [];
  for (const path of paths) {
    finalPaths.push({
      path,
      loadChildren: () =>
        import('@b2b/mobile-web-feature-page-lib').then(
          (m) => m.MOBILE_WEB_FEATURE_PAGE_ROUTES,
        ),
      data: {
        reuseRoute: true,
        step: PAGE_TYPES.FEATURE_HOME_PAGE,
      },
    });
  }

  return finalPaths;
};

const setListPageRoutes = (
  paths: string[],
  suffixPath: string,
  isDesktop: boolean,
): Routes => {
  if (isDesktop) {
    return paths.map((path) => {
      return {
        path: path + suffixPath,
        loadChildren: () =>
          import('@b2b/desktop-web-property-list-lib').then(
            (m) => m.DESKTOP_WEB_PROPERTY_LIST_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.LIST_PAGE,
        },
      };
    });
  } else {
    return paths.map((path) => {
      return {
        path: path + suffixPath,
        loadChildren: () =>
          import('@b2b/mob-web-property-list-lib').then(
            (m) => m.MOBILE_WEB_PROPERTY_LIST_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.LIST_PAGE,
        },
      };
    });
  }
};

const setDetailPageRoutes = (
  paths: string[],
  suffixPath: string,
  isDesktop: boolean,
): Routes => {
  if (isDesktop) {
    return paths.map((path) => {
      return {
        path: path + suffixPath,
        loadChildren: () =>
          import('@b2b/desktop-web-property-detail-lib').then(
            (m) => m.DESKTOP_DETAIL_PAGE_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.DETAIL_PAGE,
        },
      };
    });
  } else {
    return paths.map((path) => {
      return {
        path: path + suffixPath,
        loadChildren: () =>
          import('@b2b/mobile-web-property-detail-lib').then(
            (m) => m.MOB_WEB_DETAIL_PAGE_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.DETAIL_PAGE,
        },
      };
    });
  }
};

const setActivityDetailPageRoutes = (
  paths: string[],
  suffixPath: string,
  isDesktop: boolean,
): Routes => {
  if (isDesktop) {
    return paths.map((path) => {
      return {
        path: path + suffixPath,
        loadChildren: () =>
          import('@b2b/desktop-web-activity-detail-lib').then(
            (m) => m.DESKTOP_ACTIVITY_DETAIL_PAGE_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.ACTIVITY_DETAIL_PAGE,
        },
      };
    });
  } else {
    return paths.map((path) => {
      return {
        path: path + suffixPath,
        loadChildren: () =>
          import('@b2b/mobile-web-activity-detail-lib').then(
            (m) => m.MOB_WEB_ACTIVITY_PAGE_ROUTES,
          ),
        data: {
          reuseRoute: true,
          step: PAGE_TYPES.ACTIVITY_DETAIL_PAGE,
        },
      };
    });
  }
};
